import dayjs from 'dayjs'
import {CreditPackTerms} from '../../../../services/payments'

// 표기 규칙 (정책 16.9, 2026-10-01 확정)
//  - 날짜: 문장은 "2027년 9월 30일"(월 · 일 앞 0 없음), 표 · 목록 행은 "2027-09-30"
//  - 숫자: 크레딧과 금액은 천 단위 쉼표

export function formatNumber(value: number | string | null | undefined) {
    return Number(value ?? 0).toLocaleString('ko-KR')
}

/** 문장 날짜 — 2027년 9월 30일. 입력은 yyyy-MM-dd(LocalDate) 또는 dayjs */
export function formatSentenceDate(date: string | dayjs.Dayjs) {
    return dayjs(date).format('YYYY년 M월 D일')
}

/** 표 · 목록 행 날짜 — 2027-09-30 */
export function formatRowDate(date: string | dayjs.Dayjs) {
    return dayjs(date).format('YYYY-MM-DD')
}

/**
 * 오늘 구매할 때의 소멸일 — 구매일(한국 날짜)에 소멸 기한(개월)을 더한다. 없는 날짜는 그 달 말일이다
 * (dayjs 의 month 가산이 말일로 맞춘다. BE CreditPackFifoCalculator.expiryOf 의 plusMonths 와 같다).
 * 일수로 바꿔 계산하지 않는다(#279 terms 규칙).
 */
export function expiryOfPurchaseToday(expiryMonths: number) {
    const todayInSeoul = dayjs().tz('Asia/Seoul').format('YYYY-MM-DD')
    return dayjs(todayInSeoul).add(expiryMonths, 'month')
}

/** 소멸 기한 — 12 → "1년", 18 → "18개월" */
export function formatExpiryPeriod(expiryMonths: number) {
    return expiryMonths % 12 === 0 ? `${expiryMonths / 12}년` : `${expiryMonths}개월`
}

/** 비율(0~1) → 퍼센트 숫자. 0.1 → "10", 0.025 → "2.5" */
export function formatRate(rate: number) {
    return String(Math.round(rate * 1000) / 10)
}

/** 부가세 포함 금액 → 부가세 별도 표기(D-85). 팩 가격은 부가세 별도로 보인다 */
export function excludeVat(amount: number) {
    return Math.round(Number(amount) / 1.1)
}

/** 소멸 임박 안내 두 시점(일 전). 둘 다 있어야 고지할 수 있다 */
export function noticeDaysOf(terms: CreditPackTerms | null | undefined): [number, number] | null {
    const days = terms?.noticeDays
    if (!days || days.length < 2 || days[0] == null || days[1] == null) return null
    return [days[0], days[1]]
}

export type ProductNoticeValues = {
    expiryPeriod: string
    firstNoticeDays: number
    secondNoticeDays: number
    withdrawalPeriodDays: number
    lowUsageThreshold: string
    lowUsageRate: string
    highUsageRate: string
}

/**
 * P2 상품 고지(정책 16.3 · 약관 제10조 제3항)의 숫자 자리. 하나라도 서버에 설정이 없으면(null)
 * 숫자를 지어내지 않고 null 을 돌려준다 — 고지를 띄우지 않고, 고지 없이는 결제로 넘어가지 않는다.
 */
export function productNoticeValuesOf(terms: CreditPackTerms | null | undefined): ProductNoticeValues | null {
    const noticeDays = noticeDaysOf(terms)
    if (
        !terms ||
        !noticeDays ||
        terms.expiryMonths == null ||
        terms.withdrawalPeriodDays == null ||
        terms.lowUsageThreshold == null ||
        terms.lowUsageRate == null ||
        terms.highUsageRate == null
    ) {
        return null
    }
    return {
        expiryPeriod: formatExpiryPeriod(terms.expiryMonths),
        firstNoticeDays: noticeDays[0],
        secondNoticeDays: noticeDays[1],
        withdrawalPeriodDays: terms.withdrawalPeriodDays,
        lowUsageThreshold: formatRate(terms.lowUsageThreshold),
        lowUsageRate: formatRate(terms.lowUsageRate),
        highUsageRate: formatRate(terms.highUsageRate),
    }
}
